import { useIntl } from "react-intl";
import { AttachmentProps } from "./types";
import { Flex, GapSize } from "../flex";
import {
	Menu,
	MenuItem,
	MenuList,
	MenuPopover,
	MenuTrigger,
	MenuTriggerChildProps,
	ProgressBar,
	Text,
	mergeClasses,
} from "@fluentui/react-components";
import {
	ArrowDownload20Regular,
	Attach20Regular,
	BinRecycle20Regular,
	ChevronDownRegular,
	Eye20Regular,
	Image20Regular,
} from "@fluentui/react-icons";
import { formatBytes, isImage } from "@/common/helpers";
import { useStyle } from "./style";
import { useEffect, useMemo, useState } from "react";
import { UploadStatus } from "@/components/molecules/emailEditor";

export const Attachment = (props: AttachmentProps) => {
	const intl = useIntl();
	const classes = useStyle();

	const { name, type, size, uploadStatus, onPreview, onDownload, onRemove } =
		props;

	const [isProgressShown, setIsProgressShown] = useState(false);

	useEffect(() => {
		if (uploadStatus === UploadStatus.InProgress) {
			setIsProgressShown(true);
		}

		if (uploadStatus === UploadStatus.Completed) {
			const timeout = setTimeout(() => {
				setIsProgressShown(false);
			}, 2000);

			return () => clearTimeout(timeout);
		}

		return () => {};
	}, [uploadStatus]);

	const actionsMenu = useMemo(() => {
		const menuItems = [];

		if (!!onPreview) {
			menuItems.push(
				<MenuItem
					icon={<Eye20Regular />}
					onClick={onPreview}
					children={intl.formatMessage({
						id: "Interaction.EmailEditor.Preview",
					})}
					key={"preview"}
				/>
			);
		}

		if (!!onDownload) {
			menuItems.push(
				<MenuItem
					icon={<ArrowDownload20Regular />}
					onClick={onDownload}
					children={intl.formatMessage({
						id: "Interaction.EmailEditor.Download",
					})}
					key={"download"}
				/>
			);
		}

		if (!!onRemove) {
			menuItems.push(
				<MenuItem
					icon={<BinRecycle20Regular />}
					onClick={onRemove}
					children={intl.formatMessage({
						id: "Interaction.EmailEditor.Remove",
					})}
					key={"remove"}
				/>
			);
		}

		return <MenuList>{menuItems}</MenuList>;
	}, [onPreview, onDownload, onRemove]);

	const iconByType = useMemo(() => {
		return !type || !isImage(type) ? (
			<Attach20Regular className={classes.icon} />
		) : (
			<Image20Regular className={classes.icon} />
		);
	}, [type]);

	return (
		<Menu
			positioning={{
				position: "below",
				align: "end",
				offset: { mainAxis: 2 },
			}}
		>
			<MenuTrigger disableButtonEnhancement>
				{(buttonProps: MenuTriggerChildProps<"button">) => (
					<Flex column className={classes.root}>
						<Flex className={classes.buttonsContainer}>
							<button
								className={classes.button}
								onClick={() => {
									if (!!onPreview) {
										onPreview();
									} else if (!!onDownload) {
										onDownload();
									}
								}}
							>
								<Flex vAlign="center" gap={GapSize.Small}>
									{iconByType}
									<Flex column className={classes.middleContainer}>
										<Text
											size={200}
											truncate
											wrap={false}
											title={name}
											children={name}
										/>
										<Text size={100} children={formatBytes(size)} />
									</Flex>
								</Flex>
							</button>
							<button
								{...buttonProps}
								className={mergeClasses(classes.button, classes.buttonMenu)}
								children={<ChevronDownRegular fontSize={16} />}
								disabled={uploadStatus == UploadStatus.InProgress}
							/>
						</Flex>

						<ProgressBar
							className={
								isProgressShown
									? classes.progressBar
									: classes.progressBarHidden
							}
							value={uploadStatus == UploadStatus.InProgress ? 0.3 : 1}
						/>
					</Flex>
				)}
			</MenuTrigger>
			<MenuPopover children={actionsMenu} />
		</Menu>
	);
};
